import React, { useState } from 'react';
import { Box, Text } from 'ink';
import type { AgentTreeNode } from '../../types';
import { useInputAction, usePublishInputContext } from '../keymap';
import { useTheme } from '../theme';
import { ControlsHint } from './ControlsHint';

const PANEL_PADDING_X = 1;

export interface SubsessionChord {
  /** True while the chord panel owns the prompt area. */
  active: boolean;
  /** The highlighted row. */
  selectedChildNumber: number;
}

/**
 * Owns the Ctrl+T gesture over the viewed agent's children. One child: Ctrl+T
 * jumps straight in. Several: Ctrl+T opens a modal picker in the prompt area.
 */
export function useSubsessionChord(
  childCount: number,
  onEnter: (childNumber?: number) => void,
): SubsessionChord {
  const [active, setActive] = useState(false);
  const [selectedChildNumber, setSelectedChildNumber] = useState(1);
  usePublishInputContext({ subsessionChordArmed: active });

  const open = (childNumber?: number) => {
    setActive(false);
    onEnter(childNumber);
  };

  useInputAction('subsession.enter', () => {
    if (childCount <= 1) {
      onEnter();
      return;
    }
    setSelectedChildNumber(1);
    setActive(true);
  });
  useInputAction('subsession.enterChild', (event) => {
    const childNumber = Number(event.name);
    // A digit past the child list keeps the panel up rather than dismissing on a typo.
    if (childNumber <= childCount) open(childNumber);
  });
  useInputAction('subsession.chordPrev', () =>
    setSelectedChildNumber((current) => Math.max(1, current - 1)),
  );
  useInputAction('subsession.chordNext', () =>
    setSelectedChildNumber((current) => Math.min(childCount, current + 1)),
  );
  useInputAction('subsession.chordConfirm', () => open(selectedChildNumber));
  useInputAction('subsession.chordCancel', () => setActive(false));
  useInputAction('subsession.chordSwallow', () => {});

  return { active, selectedChildNumber };
}

export const SubsessionChordPanel: React.FC<{
  subagents: readonly AgentTreeNode[];
  selectedChildNumber: number;
  columns?: number;
}> = ({ subagents, selectedChildNumber, columns }) => {
  const { tokens } = useTheme();

  return (
    <Box
      flexDirection="column"
      borderStyle="single"
      borderColor={tokens.text.subtle}
      borderLeft={false}
      borderRight={false}
      paddingX={PANEL_PADDING_X}
    >
      {subagents.map((node, index) => {
        const childNumber = index + 1;
        const isSelected = childNumber === selectedChildNumber;
        return (
          <Text
            key={node.id}
            color={isSelected ? tokens.text.accent : tokens.text.default}
            bold={isSelected}
          >
            {isSelected ? '› ' : '  '}
            {childNumber <= 9 ? `${childNumber} ` : '  '}
            {node.displayName}
          </Text>
        );
      })}
      <ControlsHint
        availableWidth={columns === undefined ? undefined : columns - 2 * PANEL_PADDING_X}
      >
        {'**↑↓** + **Enter** or a number key to select the session · **Esc** to cancel'}
      </ControlsHint>
    </Box>
  );
};
